/**
 * Account security state: the WorkOS sessions behind this identity.
 *
 * Same idiom as `state/orgProfile.ts` — a cached atom over the workspace
 * server's `/api/amoreg/account/security`, which forwards to the domain API
 * where the WorkOS calls happen. Revoking refreshes the list.
 *
 * @module state/accountSecurity
 */
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useMemo } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";
import {
  reasonFromBody,
  UNAVAILABLE_MESSAGES,
  type OrgProfileUnavailableReason,
} from "./orgProfile";

const SessionSchema = Schema.Struct({
  id: Schema.String,
  status: Schema.String,
  authMethod: Schema.String,
  device: Schema.String,
  ipAddress: Schema.NullOr(Schema.String),
  createdAt: Schema.NullOr(Schema.String),
  expiresAt: Schema.NullOr(Schema.String),
});
export type AccountSession = typeof SessionSchema.Type;

const SecuritySchema = Schema.Struct({
  sessions: Schema.Array(SessionSchema),
  passkeysManageable: Schema.Boolean,
});
export type AccountSecuritySnapshot = typeof SecuritySchema.Type;

const decodeSecurity = Schema.decodeUnknownEffect(SecuritySchema);

const securityUrl = () => resolvePrimaryEnvironmentHttpUrl("/api/amoreg/account/security");

const fetchSecurity = Effect.gen(function* () {
  const response = yield* Effect.tryPromise(() =>
    fetch(securityUrl(), { credentials: "include" }),
  );
  if (!response.ok) {
    const body = yield* Effect.tryPromise(() => response.text()).pipe(
      Effect.orElseSucceed(() => ""),
    );
    return { unavailable: reasonFromBody(body) } as const;
  }
  const json = yield* Effect.tryPromise(() => response.json());
  return yield* decodeSecurity(json);
}).pipe(Effect.orElseSucceed(() => null));

const securityRuntime = Atom.runtime(Layer.empty);
const securityAtom = securityRuntime
  .atom(fetchSecurity)
  .pipe(Atom.withLabel("web-amoreg:account-security"));

export interface AccountSecurityView {
  readonly security: AccountSecuritySnapshot | null;
  /** Set when there is nothing to show, naming why. */
  readonly unavailableMessage: string | null;
  /** False when the identity provider is not reachable for this session. */
  readonly available: boolean;
  readonly isPending: boolean;
  readonly refresh: () => void;
  /** Revoke one sign-in; resolves to an error message, or null on success. */
  readonly revokeSession: (sessionId: string) => Promise<string | null>;
}

export function useAccountSecurity(): AccountSecurityView {
  const result = useAtomValue(securityAtom);

  const refresh = useCallback(() => {
    appAtomRegistry.refresh(securityAtom);
  }, []);

  const revokeSession = useCallback(async (sessionId: string): Promise<string | null> => {
    const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/account/security/revoke", {
      sessionId,
    });
    try {
      const response = await fetch(url, { method: "POST", credentials: "include" });
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        try {
          const parsed = JSON.parse(text) as { message?: string };
          return parsed.message ?? "That sign-in could not be ended.";
        } catch {
          return text.length > 0 ? text : "That sign-in could not be ended.";
        }
      }
      appAtomRegistry.refresh(securityAtom);
      return null;
    } catch {
      return "That sign-in could not be ended.";
    }
  }, []);

  return useMemo(() => {
    const value = Option.getOrNull(AsyncResult.value(result)) ?? null;
    const security = value !== null && "unavailable" in value ? null : value;
    const reason: OrgProfileUnavailableReason | null =
      value !== null && "unavailable" in value ? value.unavailable : null;
    return {
      security,
      unavailableMessage: reason === null ? null : UNAVAILABLE_MESSAGES[reason],
      available: security !== null,
      isPending: result.waiting && value === null,
      refresh,
      revokeSession,
    };
  }, [refresh, result, revokeSession]);
}
